<template>
  <b-modal
    body-class="padding"
    button-size="sm"
    ok-title="Save"
    size="lg"
    title="Share patient scan with"
    title-tag="h5"
    cancel-title="Cancel"
    id="idShareScanModal"
    cancel-variant="outline-primary"
    @ok="onOk"
    @show="getSelectedUsers"
  >
    <b-overlay :show="loading" rounded="sm">
      <div class="padding" @submit.stop.prevent="handleSubmit">
        <b-row>
          <span class="header">Makosuite Users:</span>
        </b-row>
        <b-row>
          <b-form-input
            list="input-list"
            id="input-with-list"
            size="sm"
            v-model="currentSelectedUser"
            placeholder="Type to Search"
            @change="addSelectedUser()"
          ></b-form-input>
          <b-form-datalist
            id="input-list"
            :options="makosuiteUserListModified"
          ></b-form-datalist>
        </b-row>
        <b-row>
          <b-form-select
            size="sm"
            id="idMakosuiteUsers"
            v-model="selectedMakosuiteUsers"
            multiple
            required
            :options="makosuiteUserListModified"
            :select-size="10"
          ></b-form-select>
        </b-row>
        <b-row>
          <span class="header">Selected Users:</span>
        </b-row>
        <b-row>
          <template v-for="user in selectedMakosuiteUsers">
            <b-badge pill variant="primary" class="user-badge" :key="user"
              >{{ user }}
              <b-icon
                icon="x"
                scale="1.3"
                @click="deleteSelectedUser(user)"
              ></b-icon
            ></b-badge>
          </template>
        </b-row>
      </div>
    </b-overlay>
  </b-modal>
</template>

<script>
export default {
  data() {
    return {
      currentSelectedUser: "",
      selectedMakosuiteUsers: [],
      makosuiteUserList: [],
      makosuiteUserListModified: [],
      allUserEmails: [],
      loading: true,
    };
  },
  props: ["patientScanId"],
  created() {
    this.getAllMakosuiteUsers();
  },
  components: {},
  methods: {
    async onOk() {
      const metadataResponse =
        await this.$patientScansMetadataApi.patientscansIdMetadataGet(
          this.patientScanId
        );

      // If scan has already been shared, replace the new shared users in the existing
      // json and use PUT (so that previously shared users can be un-shared with)
      let additionalData = metadataResponse.additionalData;
      let existingSharedUsers = false;
      try {
        additionalData.jointReplacement.sharedScan.users =
          this.selectedMakosuiteUsers;
        existingSharedUsers = true;
      } catch {
        // Otherwise, build up the json and use PATCH
        additionalData = '{"jointReplacement" : { "sharedScan" : { "users" : [';
        this.selectedMakosuiteUsers.forEach((user) => {
          additionalData += '"' + user + '",';
        });
        additionalData = additionalData.slice(0, -1);
        additionalData += "]}}}";
      }

      try {
        if (existingSharedUsers) {
          await this.$patientScansMetadataApi.patientscansIdMetadataPut(
            this.patientScanId,
            additionalData
          );
        } else {
          await this.$patientScansMetadataApi.patientscansIdMetadataPatch(
            this.patientScanId,
            additionalData
          );
        }
        this.$root.$emit("showAlert", {
          type: "info",
          msg: "Scan shared successfully.",
        });
        // Refresh patient scans so shared icon will update
        this.$emit("refreshPatientScans", {});
      } catch (error) {
        this.$root.$emit("showAlert", {
          type: "error",
          msg: `Scan failed to share with error: ${error.status}`,
        });
      }
      // Reset the selection
      this.selectedMakosuiteUsers = [];
    },
    async getAllMakosuiteUsers() {
      try {
        this.loading = true;
        const allRawMakosuiteUsers = await this.$buhUserApi.usersGet();
        // Adjust the list based on branch black list - users with branches on the black list
        // can only see and share with other users on the black list (US branches)
        // Users with NO branches on the black list (EU branches) cannot share with
        // users on the black list (US branches)
        const adjustedMakosuiteUsers = await this.adjustUsersFromBlackList(
          allRawMakosuiteUsers
        );

        // Format and sort the users, and parse into options for select
        this.makosuiteUserList = this.capitalizeUserNames(
          adjustedMakosuiteUsers
        );
        this.makosuiteUserList.sort(this.compareUsersByLastName);
        this.allUserEmails = [];
        this.makosuiteUserListModified = this.makosuiteUserList.map((user) => {
          if (user.userEmail) {
            const formattedName = user.lastName + ", " + user.firstName;
            this.allUserEmails.push(user.userEmail);
            return {
              text: formattedName,
              value: user.userEmail,
            };
          }
        });
      } catch (error) {
        this.$root.$emit("showAlert", {
          type: "error",
          msg: `Failed to retrieve Makosuite users with error: ${error.status}`,
        });
      }
    },
    async getSelectedUsers() {
      // Get all the previously selected users
      this.loading = true;
      this.selectedMakosuiteUsers = [];
      const metadataResponse =
        await this.$patientScansMetadataApi.patientscansIdMetadataGet(
          this.patientScanId
        );
      try {
        this.selectedMakosuiteUsers =
          metadataResponse.additionalData.jointReplacement.sharedScan.users ??
          [];
      } catch {
        this.selectedMakosuiteUsers = [];
      }
      this.loading = false;
    },
    addSelectedUser() {
      if (this.allUserEmails.includes(this.currentSelectedUser)) {
        this.selectedMakosuiteUsers.push(this.currentSelectedUser);
        this.currentSelectedUser = "";
      }
    },
    deleteSelectedUser(user) {
      this.selectedMakosuiteUsers = this.selectedMakosuiteUsers.filter(
        (item) => item !== user
      );
    },
    // ----------------------- Private Helper Methods -----------------------
    capitalizeUserNames(userList) {
      let returnList = [];
      userList.forEach((user) => {
        if (user.userEmail) {
          returnList.push({
            ...user,
            firstName:
              user.firstName.charAt(0).toUpperCase() + user.firstName.slice(1),
            lastName:
              user.lastName.charAt(0).toUpperCase() + user.lastName.slice(1),
          });
        }
      });
      return returnList;
    },
    compareUsersByLastName(user1, user2) {
      // First compare by last name
      if (user1.lastName < user2.lastName) return -1;
      if (user1.lastName > user2.lastName) return 1;
      // On last name equal, compare first name
      if (user1.firstName < user2.firstName) return -1;
      if (user1.firstName > user2.firstName) return 1;
      // On first and last name equal, compare email
      if (user1.userEmail < user2.userEmail) return -1;
      if (user1.userEmail > user2.userEmail) return 1;
      return 0;
    },
    async adjustUsersFromBlackList(allUsers) {
      let blackList = JSON.parse(
        this.$shareScanBranchExternalReferencesBlackList
      );
      // Only needs adjusting if there are any branches on blacklist
      if (blackList && blackList.length > 0) {
        blackList = blackList.map((number) => number.toString());
        let usersOnBlackList = [];
        let usersNotOnBlackList = [];
        allUsers.forEach((user) => {
          const userBranchExternalRefs = user.branches.map(
            (branch) => branch.externalReference
          );
          // Add a user to the black list if any of their branches are on the black list
          if (userBranchExternalRefs.some((item) => blackList.includes(item))) {
            usersOnBlackList.push(user);
          } else {
            usersNotOnBlackList.push(user);
          }
        });

        // Check if current user is on black list
        const thisUserOnBlackList = usersOnBlackList.some(
          (user) => user.userEmail === this.$msal.data.user.userName
        );

        // If the user is in a branch on the blacklist, they can only see other users on the blacklist
        if (thisUserOnBlackList) {
          return usersOnBlackList
        } else {
          return usersNotOnBlackList;
        }
      }
    },
  },
};
</script>

<style scoped>
.padding {
  padding: 0rem 2rem 1rem 2rem;
}
.header {
  margin: 0.15rem 0.5rem;
}
.user-badge {
  font-weight: 400;
  font-size: 0.9rem;
  margin: 0.2rem;
}

#idMakosuiteUsers {
  margin-bottom: 1rem;
}
</style>